import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RecipeView from '../components/RecipeView';
import { UpgradeModal } from '../components/UpgradeModal';
import { toast } from 'sonner';
import { 
    Sparkles, ArrowRight, ArrowLeft, Loader2, Bookmark, Zap, Brain, Search, 
    Tag, Globe, Folder, Play, FileText, ExternalLink, 
    MessageSquare, CheckCircle2, Plus
} from 'lucide-react';
import { getInlineEmbed } from '../lib/media-embed';
import InlineMediaPlayer from '../components/InlineMediaPlayer';

const API = '/api';

const FEATURES = [
    { icon: Zap, title: 'Instant AI Summaries', desc: 'Get the key points from any link in seconds' },
    { icon: Brain, title: 'Ask BrainSync About Your Saves', desc: 'Chat with your bookmarks using RAG search' },
    { icon: Tag, title: 'Auto-Tagging', desc: 'AI categorizes and tags content automatically' },
    { icon: Search, title: 'Smart Search', desc: 'Find anything across all your saved content' },
];

const isHebrew = (text) => {
    if (!text) return false;
    return (text.match(/[\u0590-\u05FF]/g) || []).length > (text.match(/[a-zA-Z]/g) || []).length;
};
const isRussian = (text) => {
    if (!text) return false;
    return (text.match(/[\u0400-\u04FF]/g) || []).length > (text.match(/[a-zA-Z]/g) || []).length;
};
const isRTL = (text) => isHebrew(text);
const getLocale = (text) => {
    if (isHebrew(text)) return 'he';
    if (isRussian(text)) return 'ru';
    return 'en';
};
const L = {
    readMore: { en: 'Read more →', he: 'קרא עוד ←', ru: 'Читать далее →' },
    whyMatters: { en: 'Why This Matters', he: 'למה זה חשוב', ru: 'Почему это важно' },
};

const SummaryLine = ({ line }) => {
    const trimmed = line.trim();
    if (!trimmed) return null;
    const lineRTL = isRTL(trimmed);
    const lineDir = lineRTL ? 'rtl' : 'ltr';
    const locale = getLocale(trimmed);

    // Section headers (English Summary, Hebrew Summary, English Translation, etc.)
    if (/^(English Summary|English Translation|ENGLISH SUMMARY|ENGLISH TRANSLATION|HEBREW SUMMARY|RUSSIAN SUMMARY|סיכום באנגלית|תרגום לאנגלית)[:\s]*$/i.test(trimmed)) {
        return (
            <div className="mt-6 pt-5 border-t border-gray-200">
                <p className="text-sm font-semibold text-emerald-600 mb-3 flex items-center gap-2" dir={lineDir}>
                    {trimmed.replace(':', '')}
                </p>
            </div>
        );
    }

    // "Why this matters" section
    const whyMatch = trimmed.match(/^(WHY\s+(THIS\s+)?MATTERS|Why this matters|למה זה חשוב|Почему это важно)[:\s]*(.*)/i);
    if (whyMatch) {
        const content = whyMatch[3] || '';
        return (
            <div className="mt-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100" dir={isRTL(content || whyMatch[1]) ? 'rtl' : 'ltr'}>
                <p className="text-emerald-700 text-xs font-bold uppercase tracking-wider mb-1.5">
                    {L.whyMatters[locale]}
                </p>
                <p className="text-gray-700 text-base leading-relaxed">{content}</p>
            </div>
        );
    }

    // Bullet points
    if (/^[•·\-\*]\s/.test(trimmed)) {
        const content = trimmed.replace(/^[•·\-\*]\s*/, '');
        return (
            <div className={`flex items-start gap-2.5 ${lineRTL ? 'flex-row-reverse text-right' : ''}`} dir={lineDir}>
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-[10px] flex-shrink-0" />
                <p className="text-sm leading-relaxed">{content}</p>
            </div>
        );
    }

    // Header lines (short lines ending with colon)
    if (trimmed.includes(':') && trimmed.length < 60 && !trimmed.match(/^(http|www)/i)) {
        const idx = trimmed.indexOf(':');
        const label = trimmed.substring(0, idx).trim();
        const content = trimmed.substring(idx + 1).trim();
        if (label && content) {
            return (
                <p dir={lineDir}>
                    <span className="font-semibold text-gray-700">{label}:</span> {content}
                </p>
            );
        }
    }

    return <p dir={lineDir}>{trimmed}</p>;
};

const SharePage = () => {
    const { bookmarkId } = useParams();
    const navigate = useNavigate();
    const { user, setUser } = useAuth();
    const [bookmark, setBookmark] = useState(null);
    const [sharerName, setSharerName] = useState(null);
    const [sharerUsername, setSharerUsername] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [creatingAccount, setCreatingAccount] = useState(false);
    const [summaryExpanded, setSummaryExpanded] = useState(false);
    // Breadcrumb back to the originating public collection, if any.
    // PublicCategoryPage stashes this on click (`share_referrer` key in
    // sessionStorage) and we surface it as a small chip at the top of
    // the share page so the visitor knows they came from a curator's
    // library and can one-tap back.
    const [collectionRef, setCollectionRef] = useState(null);
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem('share_referrer');
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (parsed && parsed.bookmark_id === bookmarkId && parsed.category_id) {
                setCollectionRef(parsed);
            }
        } catch (_e) { /* ignore */ }
    }, [bookmarkId]);

    useEffect(() => {
        // Capture referral tag before anything else so even shadow account
        // flows (create-shadow endpoint) can benefit down the line.
        try {
            const params = new URLSearchParams(window.location.search);
            const ref = params.get('ref');
            if (ref && /^user_[a-z0-9]+$/i.test(ref)) {
                localStorage.setItem('brainsync_ref', ref);
            }
        } catch (_) { /* non-browser env */ }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch(`${API}/share/${bookmarkId}/data`);
                if (res.ok) {
                    const data = await res.json();
                    setBookmark(data.bookmark);
                    setSharerName(data.sharer_name);
                    setSharerUsername(data.sharer_username);
                } else {
                    setError(true);
                }
            } catch (e) {
                console.warn('Failed to fetch shared bookmark:', e);
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [bookmarkId]);

    const getDomain = (url) => {
        try { return new URL(url).hostname.replace('www.', ''); } catch { return ''; }
    };

    const getThumbnailUrl = (thumb) => {
        if (!thumb) return null;
        if (thumb.startsWith('http')) return thumb;
        if (thumb.startsWith('/api/')) return thumb;
        return `/api/thumbnails/${thumb}`;
    };

    const handleTryBrainSync = useCallback(async () => {
        // Where to land after the user accepts a shadow account: if they
        // arrived via a collection breadcrumb, drop them on that
        // collection page so the curator's full library is one tap away.
        // Otherwise the bookmark detail page (so they immediately see
        // their freshly-saved item) - falling back to /dashboard.
        const landingPath = collectionRef?.category_id
            ? `/c/${collectionRef.category_id}`
            : '/dashboard';
        if (user && !user.is_shadow) { navigate(landingPath); return; }
        setCreatingAccount(true);
        try {
            const res = await fetch(`${API}/share/create-shadow`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ source_bookmark_id: bookmarkId }),
            });
            if (res.ok) { const data = await res.json(); setUser(data.user); navigate(landingPath); }
        } catch (e) { console.warn('Shadow account creation failed:', e); window.location.href = 'https://brainsync.pro'; }
        finally { setCreatingAccount(false); }
    }, [user, navigate, bookmarkId, setUser, collectionRef]);

    // True only for real (non-shadow) BrainSync users - these viewers
    // skip the marketing pitch and instead get one-tap "Save to my
    // BrainSync" + "Go to my Dashboard".
    const isRealUser = !!user && !user.is_shadow;
    const [cloning, setCloning] = useState(false);
    const [savingCollection, setSavingCollection] = useState(false);
    // Upsell modal - opened when the backend rejects a clone with 402
    // because the recipient is out of Premium AI quota.
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [upgradeContext, setUpgradeContext] = useState({});

    const openUpsellFromDetail = useCallback((detail) => {
        const d = detail && typeof detail === 'object' ? detail : {};
        setUpgradeContext({
            premiumUsed: typeof d.premium_used === 'number' ? d.premium_used : undefined,
            premiumLimit: typeof d.premium_limit === 'number' ? d.premium_limit : undefined,
            tier: d.tier || 'free',
            message: d.message || '',
        });
        setUpgradeOpen(true);
        if (d.message) toast.error(d.message);
    }, []);

    const handleSaveToMyBrainSync = useCallback(async () => {
        setCloning(true);
        try {
            const res = await fetch(`${API}/share/${bookmarkId}/clone`, {
                method: 'POST', credentials: 'include',
            });
            if (res.status === 402) {
                const body = await res.json().catch(() => ({}));
                openUpsellFromDetail(body && body.detail);
                return;
            }
            if (res.ok) {
                const data = await res.json();
                navigate(`/bookmark/${data.bookmark_id}`);
                return;
            }
            toast.error('Could not save this bookmark. Try again in a moment.');
        } catch (e) {
            console.warn('Clone failed:', e);
            toast.error('Save failed.');
        } finally {
            setCloning(false);
        }
    }, [bookmarkId, navigate, openUpsellFromDetail]);

    const handleSaveWholeCollection = useCallback(async () => {
        if (!collectionRef?.category_id) return;
        setSavingCollection(true);
        try {
            const res = await fetch(`${API}/public/categories/${collectionRef.category_id}/clone`, {
                method: 'POST', credentials: 'include',
            });
            if (res.status === 402) {
                const body = await res.json().catch(() => ({}));
                openUpsellFromDetail(body && body.detail);
                return;
            }
            if (res.status === 401) {
                toast.error('Please sign in again to save this collection.');
                return;
            }
            if (!res.ok) {
                toast.error('Could not save the collection. Try again in a moment.');
                return;
            }
            const json = await res.json();
            if (json.already_owned) {
                toast.info('This is your own collection.');
                return;
            }
            const added = json.added || 0;
            const skipped = json.skipped || 0;
            if (added === 0 && skipped > 0) {
                toast.success(`Already in your library (${skipped} item${skipped === 1 ? '' : 's'}).`);
            } else {
                toast.success(`Saved ${added} new item${added === 1 ? '' : 's'}${skipped ? ` (${skipped} already saved)` : ''} to your BrainSync.`);
            }
            setTimeout(() => navigate(`/dashboard?category=${json.category_id}`), 700);
        } catch (e) {
            console.warn('Collection clone failed:', e);
            toast.error('Save failed.');
        } finally {
            setSavingCollection(false);
        }
    }, [collectionRef, navigate, openUpsellFromDetail]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#f0f5f0] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
        );
    }

    if (error || !bookmark) {
        return (
            <div className="min-h-screen bg-[#f0f5f0] flex flex-col items-center justify-center px-6">
                <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center mb-4 shadow-sm">
                    <Bookmark className="w-8 h-8 text-gray-300" />
                </div>
                <h1 className="text-xl font-heading text-gray-900 mb-2" data-testid="share-error-title">Bookmark not found</h1>
                <p className="text-gray-400 text-sm mb-6 text-center">This shared bookmark may have been removed.</p>
                <a href="https://brainsync.pro" className="px-6 py-2.5 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors" data-testid="share-error-cta">Visit BrainSync</a>
            </div>
        );
    }

    const thumbnailUrl = getThumbnailUrl(bookmark.thumbnail);
    const summaryLines = (bookmark.summary || '').split('\n').filter(l => l.trim());
    const previewLines = summaryExpanded ? summaryLines : summaryLines.slice(0, 6);
    const showReadMore = summaryLines.length > 6;
    const titleIsRTL = isRTL(bookmark.title);
    const summaryLocale = getLocale(bookmark.summary);
    const originalUrl = bookmark.url || '';
    const hasRecipe = bookmark.recipe_data && Object.keys(bookmark.recipe_data).length > 0;

    return (
        <div className="min-h-screen bg-[#f0f5f0]" data-testid="share-page">
            {/* Sticky Header */}
            <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-100">
                <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
                    <a href="https://brainsync.pro" className="flex items-center gap-2">
                        <img src="/icon-192.png" alt="BrainSync" className="w-8 h-8 rounded-lg" />
                        <span className="text-gray-900 font-heading text-lg" data-testid="share-header-logo">BrainSync</span>
                    </a>
                    {!isRealUser && (
                        <button onClick={handleTryBrainSync} disabled={creatingAccount} className="px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors flex items-center gap-1.5" data-testid="header-cta-btn">
                            {creatingAccount ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Get Started Free <ArrowRight className="w-3.5 h-3.5" /></>}
                        </button>
                    )}
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-4 pb-32">
                {/* Breadcrumb back to the originating public collection.
                    Renders only when the visitor arrived via a /c/{id}
                    click (PublicCategoryPage drops a hint into
                    sessionStorage on link click). Closes the viral loop:
                    one tap → back to the curator's full library. */}
                {collectionRef && (
                    <a
                        href={`/c/${collectionRef.category_id}`}
                        className="mt-4 -mb-1 inline-flex items-center gap-2 text-[13px] font-medium text-emerald-700 hover:text-emerald-800 transition-colors"
                        data-testid="share-collection-back"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        <span className="truncate max-w-[260px] sm:max-w-none">
                            Back to <span className="font-semibold">"{collectionRef.category_name}"</span>
                            {collectionRef.curator ? <> by {collectionRef.curator}</> : null}
                        </span>
                    </a>
                )}
                {/* Shared banner */}
                <div className="mt-4 mb-5 bg-emerald-50 border border-emerald-100 rounded-full px-4 py-2.5 flex items-center justify-center gap-2" data-testid="shared-banner">
                    <img src="/icon-192.png" alt="" className="w-5 h-5 rounded" />
                    <span className="text-gray-600 text-sm">
                        {sharerName ? (
                            <>
                                {sharerUsername ? (
                                    <a
                                        href={`/${sharerUsername}`}
                                        className="font-semibold text-emerald-700 hover:text-emerald-800 underline decoration-emerald-200 underline-offset-2 hover:decoration-emerald-400 transition-colors"
                                        data-testid="sharer-profile-link"
                                    >
                                        {sharerName}
                                    </a>
                                ) : (
                                    <span className="font-semibold text-gray-800">{sharerName}</span>
                                )}
                                {' '}shared this with you via <span className="font-semibold text-gray-800">BrainSync</span>
                            </>
                        ) : (
                            <>Shared with you via <span className="font-semibold text-gray-800">BrainSync</span></>
                        )}
                    </span>
                </div>

                {/* Inline media player (YouTube / Spotify / Apple Music
                    / SoundCloud) — falls back to the click-thumbnail
                    flow below for URLs whose host doesn't support
                    iframe embedding (TikTok, Instagram, Facebook
                    video, raw .mp4, etc.). */}
                <InlineMediaPlayer
                    url={bookmark.url}
                    spotifyEmbedUrl={bookmark.spotify_embed_url}
                    tiktokVideoId={bookmark.tiktok_video_id}
                    title={bookmark.title}
                    testIdPrefix="share"
                />

                {/* Thumbnail - clickable to original (hidden when an
                    inline embed above is already handling playback) */}
                {thumbnailUrl && !getInlineEmbed(bookmark.url, bookmark.spotify_embed_url, bookmark.tiktok_video_id) && (
                    <div className="relative rounded-2xl overflow-hidden mb-5 shadow-sm" data-testid="share-hero-image">
                        <a href={originalUrl || '#'} target="_blank" rel="noopener noreferrer" className="block">
                            <img src={thumbnailUrl} alt={bookmark.title} className="w-full h-56 sm:h-72 object-cover" />
                            {/* Center play button - same size as dashboard (w-12 h-12) */}
                            {bookmark.content_type === 'video' && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
                                        <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                                    </div>
                                </div>
                            )}
                        </a>
                        {/* Type badge - small, non-clickable */}
                        {bookmark.content_type && (
                            <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm rounded-full px-2.5 py-1 flex items-center gap-1 text-white text-[11px] font-medium capitalize pointer-events-none" data-testid="type-badge">
                                {bookmark.content_type === 'video' ? <Play className="w-2.5 h-2.5 fill-white" /> : <FileText className="w-2.5 h-2.5" />}
                                {bookmark.content_type}
                            </div>
                        )}
                    </div>
                )}

                {/* Title */}
                <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-gray-900 mb-3" dir={titleIsRTL ? 'rtl' : 'ltr'} data-testid="share-title" style={{ lineHeight: '1.25' }}>
                    {bookmark.title}
                </h1>

                {/* Author + Domain */}
                <div className={`flex flex-wrap items-center gap-2 text-sm mb-4 ${titleIsRTL ? 'flex-row-reverse' : ''}`}>
                    {bookmark.author && <span className="text-gray-600" data-testid="share-author">{bookmark.author}</span>}
                    {bookmark.author && originalUrl && <span className="text-gray-300">·</span>}
                    {originalUrl && (
                        <a href={originalUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-gray-400 hover:text-emerald-600 transition-colors" data-testid="source-link">
                            <Globe className="w-3.5 h-3.5" /> {getDomain(originalUrl)}
                        </a>
                    )}
                </div>

                {/* Tags */}
                {bookmark.tags && bookmark.tags.length > 0 && (
                    <div className={`flex flex-wrap gap-2 mb-6 ${titleIsRTL ? 'flex-row-reverse' : ''}`} data-testid="share-tags">
                        {bookmark.tags.slice(0, 6).map(tag => (
                            <span key={tag} className="px-3 py-1 rounded-full border border-gray-200 text-gray-500 text-xs">#{tag}</span>
                        ))}
                    </div>
                )}

                {/* Recipe View */}
                {hasRecipe && (
                    <div className="mb-6" data-testid="share-recipe">
                        <RecipeView recipe={bookmark.recipe_data} url={bookmark.url} />
                    </div>
                )}

                {/* AI Summary */}
                {bookmark.summary && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 mb-8" data-testid="share-summary-card">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-sm">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-gray-900 text-sm font-semibold">AI Summary</p>
                                <p className="text-gray-400 text-xs">Generated by BrainSync</p>
                            </div>
                        </div>

                        <div className="text-gray-600 text-base leading-relaxed space-y-3" data-testid="share-summary">
                            {previewLines.map((line, i) => (
                                <SummaryLine key={`line-${i}-${line.slice(0,20)}`} line={line} />
                            ))}
                        </div>

                        {showReadMore && !summaryExpanded && (
                            <button onClick={() => setSummaryExpanded(true)} className="mt-3 text-emerald-600 text-sm font-semibold hover:text-emerald-700 transition-colors" data-testid="share-read-more">
                                {L.readMore[summaryLocale]}
                            </button>
                        )}
                    </div>
                )}

                {/* Onboarding Section - only for non-BrainSync users */}
                {!isRealUser && (
                <div className="mb-8" data-testid="onboarding-section">
                    <div className="bg-gradient-to-br from-emerald-500/[0.08] to-emerald-50 border border-emerald-500/[0.15] rounded-2xl p-6">
                        <div className="flex items-center gap-2 mb-1">
                            <img src="/icon-192.png" alt="" className="w-7 h-7 rounded-lg" />
                            <h2 className="text-gray-900 text-lg font-heading" style={{ lineHeight: '1.3' }}>Save smarter with BrainSync</h2>
                        </div>
                        <p className="text-gray-500 text-sm mb-5">AI-powered bookmarking that actually helps you remember what you save.</p>
                        <div className="space-y-2.5 mb-5">
                            {FEATURES.map((f) => (
                                <div key={f.title} className="flex items-center gap-3">
                                    <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                                    </div>
                                    <p className="text-gray-700 text-sm">{f.title} <span className="text-gray-400 hidden sm:inline">- {f.desc}</span></p>
                                </div>
                            ))}
                        </div>
                        <button onClick={handleTryBrainSync} disabled={creatingAccount} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold text-sm transition-all active:scale-[0.98]" data-testid="try-brainsync-btn">
                            {creatingAccount ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4" /> Try BrainSync Free <ArrowRight className="w-4 h-4" /></>}
                        </button>
                        <p className="text-center text-gray-400 text-[11px] mt-2">No sign-up required - start saving instantly</p>
                    </div>
                </div>
                )}

                <p className="text-center text-gray-400 text-xs mb-4">&copy; {new Date().getFullYear()} BrainSync</p>
            </div>

            {/* Sticky bottom CTA */}
            <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#f0f5f0] via-[#f0f5f0] to-transparent pt-6 pb-5 px-4 z-20">
                <div className="max-w-2xl mx-auto">
                    {isRealUser ? (
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                    onClick={handleSaveToMyBrainSync}
                                    disabled={cloning}
                                    className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold text-sm transition-all active:scale-[0.98] shadow-lg shadow-emerald-500/25"
                                    data-testid="save-to-my-brainsync-btn"
                                >
                                    {cloning ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Bookmark className="w-4 h-4" /> Save to my BrainSync</>}
                                </button>
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full bg-white hover:bg-gray-50 text-[#2E3A2F] font-bold text-sm transition-all active:scale-[0.98] border border-gray-200 shadow-md"
                                    data-testid="go-to-dashboard-btn"
                                >
                                    <Folder className="w-4 h-4" /> Go to my Dashboard
                                </button>
                            </div>
                            {/* Secondary: Save the whole collection - only when the
                                user arrived via a /c/{id} breadcrumb. Closes the
                                loop on collection discovery: one click and the
                                entire curator's library lands in their account. */}
                            {collectionRef?.category_id && (
                                <button
                                    onClick={handleSaveWholeCollection}
                                    disabled={savingCollection}
                                    className="flex items-center justify-center gap-2 py-2.5 rounded-full bg-emerald-50 hover:bg-emerald-100 disabled:opacity-60 text-emerald-700 font-semibold text-xs sm:text-sm transition-all active:scale-[0.98] border border-emerald-200"
                                    data-testid="save-whole-collection-btn"
                                >
                                    {savingCollection ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                        <>
                                            <Plus className="w-3.5 h-3.5" />
                                            Save &ldquo;{collectionRef.category_name}&rdquo; collection too
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    ) : (
                        <button onClick={handleTryBrainSync} disabled={creatingAccount} className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold text-sm transition-all active:scale-[0.98] shadow-lg shadow-emerald-500/25" data-testid="sticky-cta-btn">
                            {creatingAccount ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Try BrainSync - It's Free <ArrowRight className="w-4 h-4" /></>}
                        </button>
                    )}
                </div>
            </div>

            <UpgradeModal
                open={upgradeOpen}
                onClose={() => setUpgradeOpen(false)}
                onPurchaseComplete={() => setUpgradeOpen(false)}
                currentTier={upgradeContext.tier || 'free'}
                premiumUsed={upgradeContext.premiumUsed}
                premiumLimit={upgradeContext.premiumLimit}
                reason="premium_quota"
            />
        </div>
    );
};

export default SharePage;
